import { MarketingLayout } from '../components/landing-v2/MarketingLayout'
import { Hero } from '../components/landing-v2/Hero'
import { MadeWithPulls } from '../components/landing-v2/MadeWithPulls'
import { PractitionersStrip } from '../components/landing-v2/PractitionersStrip'
import { ThemeSection } from '../components/landing-v2/ThemeSection'
import { ProofCard } from '../components/landing-v2/ProofCard'
import { PricingCards } from '../components/landing-v2/PricingCards'
import { FAQ } from '../components/landing-v2/FAQ'
import { FinalCTA } from '../components/landing-v2/FinalCTA'
import { LocalBusinessSchema } from '../components/seo/LocalBusinessSchema'

// Theme 01 — Draw a card
import { PullFlipProof } from '../components/landing-v2/proofs/PullFlipProof'
import { IntentionProof } from '../components/landing-v2/proofs/IntentionProof'
import { KeepThreadProof } from '../components/landing-v2/proofs/KeepThreadProof'
import { SpreadProof } from '../components/landing-v2/proofs/SpreadProof'
import { NoStreakProof } from '../components/landing-v2/proofs/NoStreakProof'
import { ShareProof } from '../components/landing-v2/proofs/ShareProof'

// Theme 02 — Track the sky
import { MoonProof } from '../components/landing-v2/proofs/MoonProof'
import { AstroProof } from '../components/landing-v2/proofs/AstroProof'
import { NatalProof } from '../components/landing-v2/proofs/NatalProof'
import { PersonalizedProof } from '../components/landing-v2/proofs/PersonalizedProof'

// Theme 03 — Keep the thread
import { JournalProof } from '../components/landing-v2/proofs/JournalProof'
import { LiveProof } from '../components/landing-v2/proofs/LiveProof'
import { ReadingRevisitedProof } from '../components/landing-v2/proofs/ReadingRevisitedProof'

// Theme 04 — The platform
import { DeckMonthProof } from '../components/landing-v2/proofs/DeckMonthProof'
import { AudioProof } from '../components/landing-v2/proofs/AudioProof'
import { VoicesProof } from '../components/landing-v2/proofs/VoicesProof'
import { ClassesProof } from '../components/landing-v2/proofs/ClassesProof'
import { PromiseCard } from '../components/landing-v2/proofs/PromiseCard'

/** Landing-v2 composition root (final form after milestone 5).
 *  Order: Hero → MadeWithPulls → PractitionersStrip → 4 ThemeSections
 *  (themes / theme-sky / theme-thread / theme-platform) → Testimonials →
 *  PricingCards → FAQ → FinalCTA.
 *
 *  LocalBusinessSchema is preserved from landing v1 — it powers Reno-local
 *  SEO (asserted by local-seo.spec.ts). */
export function LandingPage() {
  // useRevealOnScroll is now mounted in MarketingLayout so every marketing
  // route benefits — no need to repeat it here.
  return (
    <MarketingLayout>
      <LocalBusinessSchema />
      <div>
        <Hero />
        <MadeWithPulls />
        <PractitionersStrip />

        <ThemeSection
          id="themes"
          eyebrow="01"
          title="Draw a card"
          lede="A daily ritual that takes ninety seconds — and a deeper practice that keeps unfolding."
        >
          <ProofCard
            span="default"
            aspect="tall"
            title="Pull a card"
            body="One pull a day. Animated reveal, then a quiet meaning."
            visual={<PullFlipProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            title="Set an intention"
            body="Type the question, or leave it open."
            visual={<IntentionProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            cosmicOnly
            title="Keep the thread"
            body="Save a reading and add to it later."
            visual={<KeepThreadProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            cosmicOnly
            title="3- and 5-card spreads"
            body="When one card isn't enough."
            visual={<SpreadProof />}
          />
          <ProofCard
            span="narrow"
            aspect="short"
            title="No streaks"
            body="No gamification. The ritual is the reward."
            visual={<NoStreakProof />}
          />
          <ProofCard
            span="wide"
            aspect="short"
            title="Copy your reading"
            body="Grab a link to today's pull, anytime."
            visual={<ShareProof />}
          />
        </ThemeSection>

        <ThemeSection
          id="theme-sky"
          eyebrow="02"
          title="Track the sky"
          lede="The astrology under the day, made readable."
        >
          <ProofCard
            span="default"
            aspect="short"
            title="Moon calendar"
            body="Every phase, every void of course."
            visual={<MoonProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            title="Today's weather"
            body="What's in the sky right now, in plain language."
            visual={<AstroProof />}
          />
          <ProofCard
            span="default"
            aspect="tall"
            cosmicOnly
            title="Natal chart"
            body="Yours, drawn once. Read often."
            visual={<NatalProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            cosmicOnly
            title="Personalized weather"
            body="The day's astrology, read against your birth chart — personal, not a generic horoscope."
            visual={<PersonalizedProof />}
          />
        </ThemeSection>

        <ThemeSection
          id="theme-thread"
          eyebrow="03"
          title="Keep the thread"
          lede="Practice that holds memory."
        >
          <ProofCard
            span="default"
            aspect="short"
            cosmicOnly
            title="Soul journal"
            body="Reflections that find you again."
            visual={<JournalProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            title="Live ceremonies"
            body="Gather live with Sarah and Adrienne."
            visual={<LiveProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            title="Reading revisited"
            body="Add a note, months later."
            visual={<ReadingRevisitedProof />}
          />
        </ThemeSection>

        <ThemeSection
          id="theme-platform"
          eyebrow="04"
          title="The platform"
          lede="One creator at a time. New decks and rituals as the circle grows."
        >
          <ProofCard
            span="wide"
            aspect="short"
            title="Breath of Gold"
            body="Adrienne's 67-card breath-paired deck — Cosmic-included."
            visual={<DeckMonthProof />}
          />
          <ProofCard
            span="narrow"
            aspect="short"
            title="Audio rituals"
            body="Short, embodied. Press play."
            visual={<AudioProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            title="More than one deck"
            body="Pull from The Cosmic Finder deck or Adrienne's Breath of Gold — with more on the way."
            visual={<VoicesProof />}
          />
          <ProofCard
            span="default"
            aspect="short"
            title="Classes & ceremonies"
            body="The schedule, in one place."
            visual={<ClassesProof />}
          />
          <ProofCard
            span="full"
            aspect="short"
            title="What's coming"
            body="New creators and decks, joining the circle over time — chosen with care."
            visual={<PromiseCard />}
          />
        </ThemeSection>

        <PricingCards />
        <FAQ />
        <FinalCTA />
      </div>
    </MarketingLayout>
  )
}
